// pairingGate.js — the D0 boundary (ship plan D0/D1; tasks #121, #106, #97, #107, #98).
//
// EVERYTHING the Connections tab renders passes through this module, and this
// module is pure: no React, no network, no DOM. That is what makes the core
// product guarantee — an unapproved pairing CANNOT render — testable in one
// second from node, and what keeps the UI from ever touching raw generator
// JSON. The X2-response → pairing adapter (post-verdict work) is the ONLY
// other producer of these objects.
//
// THE NORMALIZED APPROVED-PAIRING OBJECT, v1:
// {
//   pairing_version: 1,
//   scene: { movie_id, scene_number },
//   shared_moment: {                        // the pattern, writer-facing noun
//     label,                                // one sentence, no quality judgment
//     quotations: [{ text }],               // verbatim page slices
//   },
//   pairings: [{                            // the links, writer-facing noun
//     scene_mechanics_axis,                 // DER axis name (product name: Scene Mechanics)
//     craft_read_criterion,                 // traditional name (product name: Craft Read)
//     why,                                  // the mechanism, already copy-cleaned
//   }],
//   provenance: { prompt_sha256, packet_render_version, envelope, pattern_ordinal },
//   approval: {
//     approved_key,                         // sha256 hex of canonicalContent(); set by review
//     approved_by, approved_at,             // provenance of the human decision
//   },
//   suppression: null | { state }           // one of SUPPRESSION_STATES
// }
//
// D1 RULE, IMPLEMENTED AS A CONTENT HASH RATHER THAN A DB JOIN: approved_key is
// sha256 over canonicalContent(pairing) — the semantic content PLUS generator
// provenance. Regenerate the pattern, change a quotation, change the prompt or
// the render version, and the hash moves, the key mismatches, and the pairing
// silently stops rendering. Stale approval is not possible, not just forbidden.

'use strict';

// The five suppression states that can actually occur (#98). A suppressed
// pairing is withheld with its state recorded; it is never deleted.
const SUPPRESSION_STATES = [
  'writer_hidden',            // the writer chose to hide it (#108 action)
  'scene_text_changed',       // the scene was re-uploaded; quotations may not exist any more
  'upstream_rescored',        // DER or Craft Read re-ran; endpoints may have moved
  'review_revoked',           // a reviewer withdrew approval after the fact
  'renderer_guard',           // a render-time invariant failed (missing field, bad quote)
];

// The four writer actions (#108), append-only. PROVISIONAL NAMES pending #108
// final spec; the event shape is the commitment, the labels are not.
const WRITER_ACTIONS = ['helpful', 'not_helpful', 'hide', 'show_on_page'];

function stableStringify(x) {
  if (x === null || typeof x !== 'object') return JSON.stringify(x);
  if (Array.isArray(x)) return '[' + x.map(stableStringify).join(',') + ']';
  return '{' + Object.keys(x).sort().map(k => JSON.stringify(k) + ':' + stableStringify(x[k])).join(',') + '}';
}

// What the approval covers: the writer-visible semantic content AND the
// generator provenance. Nothing else — display tweaks must not invalidate.
function canonicalContent(p) {
  return stableStringify({
    // ⚠️ SCENE IS NARROWED TO scene_number ON PURPOSE (2026-08-27), and the approved corpus was
    // re-keyed to match. The scene object carries an internal movie_id; including it here would
    // force the SERVER to ship that id to the browser just so the client could recompute the key,
    // which is exactly what the "no internal ids in the front end" rule forbids. Anything added
    // back to this object must be something a customer's browser is allowed to see.
    // Changing this function INVALIDATES EVERY approved_key — re-key with eval/rekey_pairings.js
    // and verify the old keys reproduce first, or the gate silently withholds every pairing.
    scene: { scene_number: (p.scene && p.scene.scene_number) },
    shared_moment: { label: p.shared_moment.label, quotations: p.shared_moment.quotations.map(q => ({ text: q.text })) },
    pairings: p.pairings.map(l => ({
      scene_mechanics_axis: l.scene_mechanics_axis,
      craft_read_criterion: l.craft_read_criterion,
      why: l.why,
    })),
    provenance: {
      prompt_sha256: p.provenance.prompt_sha256,
      packet_render_version: p.provenance.packet_render_version,
      envelope: p.provenance.envelope,
      pattern_ordinal: p.provenance.pattern_ordinal,
    },
  });
}

// sha256 without a static `require('crypto')`, so the same file runs unchanged in the browser
// bundle and on the CLI. Browsers and Node >= 19 expose `globalThis.crypto.subtle`; Node 18 does
// not, and the alpha adapter is run by hand, so the Node-18 path has to actually work.
//
// ⚠️ THE OLD FALLBACK — `(0, eval)('require')('crypto')` — NEVER WORKED ON NODE 18, and nothing
// caught it for months because on Node >= 19 the branch is unreachable. Two reasons it fails:
// INDIRECT eval evaluates in GLOBAL scope, where `require` does not exist even in a CommonJS
// module (it is module-scoped), and the only consumers that reach this line are `.mjs` files,
// where there is no `require` at all. Symptom, from the alpha adapter on Node 18:
// `ReferenceError: require is not defined in ES module scope`, thrown from inside contentKey.
//
// The dynamic `import()` below works in both module systems and on every Node that has ESM. The
// specifier is built at run time and carries `webpackIgnore`, which is what keeps webpack from
// pulling `node:crypto` into the browser bundle — the same job the eval trick was doing, done by
// a mechanism that is actually supported. The result is memoised because contentKey runs once per
// pairing and the import is the expensive part.
let __subtlePromise = null;
function getSubtle() {
  if (globalThis.crypto && globalThis.crypto.subtle) return Promise.resolve(globalThis.crypto.subtle);
  if (!__subtlePromise) {
    const spec = 'node:' + 'crypto';                        // built at run time on purpose
    __subtlePromise = import(/* webpackIgnore: true */ spec).then(m => (m.webcrypto || m.default.webcrypto).subtle);
  }
  return __subtlePromise;
}
async function contentKey(p) {
  const data = new TextEncoder().encode(canonicalContent(p));
  const subtle = await getSubtle();
  const buf = await subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// V1 COPY RULES (#97, #107), applied at the boundary, not in JSX: the object
// handed to the component is already clean, so a future component cannot
// forget. Valence words and repair-advice verbs are the generator's vocabulary,
// not the card's.
// First real adapter run (2026-08-12) proved the original rules wrong in both
// directions: they blocked "could generate tension" — ordinary craft prose, you
// cannot ban the word tension from a screenwriting product — and they missed
// "seen by DER as..." — an internal instrument name leaking to a writer. The
// rules now target the actual leaks: the valence CLASSIFIER used as a label
// (not the words as prose), internal instrument names (writer-facing names are
// Scene Mechanics and Craft Read), and repair advice.
// ⚠️ THE `traditional …` PHRASE RULE HAD A HOLE, AND 22 OF 98 APPROVED STRINGS WENT THROUGH IT
// (measured 2026-08-26 against eval/results/alpha_review/approved_pairings.json). It required
// "traditional" to be FOLLOWED by analysis/criterion/criteria/notes/instrument, so the form the
// generator actually produces — the bare proper noun — sailed past: "the Traditional finding of
// minimal stakes", "(Traditional engagement)", "Traditional emotional impact". Rendered in the
// Blade scene modal these print an internal instrument name straight to a writer, which §2 of
// SCENE_SCREEN_DESIGN_BRIEF says "must never appear anywhere a writer can see, and the render
// gate enforces it". It did not. This is the same class of miss the 2026-08-12 revision fixed for
// "seen by DER as…", one spelling later.
//
// THE RULE IS CASE-SENSITIVE ON PURPOSE, and that is what makes it safe. Lowercase "traditional"
// is ordinary screenwriting prose ("a traditional three-act structure") and banning it would
// repeat the original mistake of banning "tension". Capital-T Traditional mid-sentence is the
// instrument being named. Measured on the same corpus: 22 capitalised hits, ZERO lowercase hits,
// ZERO sentence-initial (where the capital would be innocent). If a sentence-initial "Traditional
// coverage often…" ever appears it will be withheld — the right direction for a boundary, since a
// withheld pattern is recoverable by regeneration and review, and a leaked one is not.
const FORBIDDEN_COPY = [
  /\bvalence\b/i,                                            // the field name itself
  /\b(reinforcing|tensioning)\b(?= (?:link|pair|pattern|finding|relationship))/i,  // classifier-as-label (#107)
  /\bthese (?:findings|analyses) are (?:reinforcing|tensioning)\b/i,
  /\bDER\b/,                                                 // internal names (product says Scene Mechanics)
  /\btraditional (?:analysis|criterion|criteria|notes?|instrument)\b/i,  // product says Craft Read
  /\bTraditional\b/,                                         // …and the bare proper noun — see above
  /\byou should\b/i, /\bconsider (adding|cutting|revising)\b/i,        // repair advice (#97)
  /\bfix(?:ing)? this\b/i,
  // ── BAND / SCORE JARGON ─────────────────────────────────────────────────────────────────────
  // §2 of SCENE_SCREEN_DESIGN_BRIEF lists this as a hard rule the gate enforces. The gate had NO
  // rule for it at all, and the corpus contains "The Traditional conflict score of 1", "rates
  // emotional impact as mild", "surface_clarity score of 9" (3 strings / 2 patterns, 2026-08-26).
  // ⚠️ THE RULE TARGETS THE CONSTRUCTION, NOT THE WORD, and that is deliberate: this is a
  // SCREENWRITING product, where "the score swells" is ordinary page prose. Banning `\bscore\b`
  // would be the "tension" mistake for a third time. The bare word goes on the review list in
  // COPY_RISKS below instead, where a human decides.
  /\b(?:score|rating)\s+of\s+\d/i,
  /\bscores?\s+(?:it\s+)?\d/i,
  /\b\d+\s*\/\s*10\b/,
  /\brates\s+[\w\s]{1,30}?\s+as\b/i,
  // A raw snake_case key is an internal field name, never something a writer typed. "…and Craft
  // Read's surface_clarity score of 9" is the actual corpus string.
  /\b[a-z]+_[a-z]+\b/,
];
function copyViolations(p) {
  const texts = [p.shared_moment.label, ...p.pairings.map(l => l.why)];
  const hits = [];
  for (const t of texts) for (const re of FORBIDDEN_COPY) if (re.test(t || '')) hits.push({ text: t, rule: String(re) });
  return hits;
}

// ── EPISTEMIC RISK: FLAGGED, NEVER AUTO-REJECTED ─────────────────────────────────────────────
// The house split (CLAUDE.md, "Copy discipline"): mechanical constraints get a validator that
// throws; epistemic ones get a REVIEW QUEUE, because context sometimes justifies the word and an
// auto-reject would silently delete a legitimate finding. FORBIDDEN_COPY above is the mechanical
// half. This is the other half, and the gate deliberately does not call it.
//
// What it is for: this product's whole claim is careful qualification — the bridge produces
// "another approved reading found the same page moment", NOT a causal account. The approved corpus
// already contains "which directly causes the Scene Mechanics finding of no page-visible force"
// (9179 s36, found 2026-08-26), which asserts exactly the causation the instrument does not
// establish. That is a review call, not a regex call: one reviewer looking at the sentence can
// tell "causes" from "the character causes the door to open". So it is surfaced, not blocked.
//
// Feed this to the review UI (C1/#93) so a reviewer sees the risky strings before approving. It
// returns [] for clean content, so a caller that ignores it loses nothing today.
const COPY_RISKS = [
  { rule: 'causal', re: /\b(caus(?:e|es|ed|ing)|because of this|results? in|leads? to)\b/i },
  { rule: 'certainty', re: /\b(always|never|every scene|proves?|definitiv\w+|guarantee\w*)\b/i },
  { rule: 'comparison', re: /\b(better than|worse than|cheaper than|replaces)\b/i },
  // The bare measurement words. Blocking them would be wrong in a screenwriting product ("the
  // score swells", "a strong rating from the guild"), but a mechanism sentence that reaches for
  // one is usually about to quote a number at the writer — which §2 forbids in convergence copy.
  // Surfaced for the reviewer; the unambiguous constructions are blocked outright above.
  { rule: 'measurement', re: /\b(score|scores|scored|rating|rates|band)\b/i },
];
function copyRisks(p) {
  const texts = [p.shared_moment && p.shared_moment.label, ...(p.pairings || []).map(l => l.why)];
  const hits = [];
  for (const t of texts) for (const r of COPY_RISKS) if (r.re.test(t || '')) hits.push({ text: t, rule: r.rule });
  return hits;
}

// THE GATE. Returns { renderable: [...], withheld: [{pairing, why}] }.
// Everything the tab shows comes from `renderable`; nothing else is ever
// passed to the component. Async because of contentKey.
async function gatePairings(pairings) {
  const renderable = [], withheld = [];
  for (const p of pairings || []) {
    if (!p || p.pairing_version !== 1) { withheld.push({ p, why: 'bad_version' }); continue; }
    if (p.suppression && p.suppression.state) { withheld.push({ p, why: 'suppressed:' + p.suppression.state }); continue; }
    if (!p.approval || !p.approval.approved_key) { withheld.push({ p, why: 'unapproved' }); continue; }
    const key = await contentKey(p);
    if (key !== p.approval.approved_key) { withheld.push({ p, why: 'approval_stale' }); continue; }
    if (!p.shared_moment || !p.shared_moment.label || !(p.pairings || []).length) { withheld.push({ p, why: 'renderer_guard' }); continue; }
    const copy = copyViolations(p);
    if (copy.length) { withheld.push({ p, why: 'copy_rule', copy }); continue; }
    renderable.push(p);
  }
  return { renderable, withheld };
}

// Append-only writer event (#108). The caller supplies the transport; this
// builds the immutable record. Events are never updated or deleted.
function writerEvent(pairing, action, extra) {
  if (!WRITER_ACTIONS.includes(action)) throw new Error('unknown writer action: ' + action);
  return {
    v: 1, action,
    approved_key: pairing.approval && pairing.approval.approved_key,
    // The script is named by HASH; the server resolves it and ignores any id in the body.
    // `pairing.scene` no longer carries movie_id — see canonicalContent() above.
    movie_hash: pairing.movie_hash,
    scene: pairing.scene,
    provenance_envelope: pairing.provenance && pairing.provenance.envelope,
    extra: extra || null,
  };
}

module.exports = { SUPPRESSION_STATES, WRITER_ACTIONS, canonicalContent, contentKey, gatePairings, writerEvent, copyViolations, copyRisks };
